const HONORIFICS = /^(dr|mr|mrs|ms|prof|professor|ambassador|amb|hon|sir|rev|eng)\.?$/i;

/**
 * Derive clean two-letter initials from a full name, ignoring honorifics
 * (Dr., Ambassador…), lone initials ("J.") and trailing credentials
 * (", PE, PMP").
 */
// This utility is exported for the leadership data tests as well as this component.
// eslint-disable-next-line react-refresh/only-export-components
export function getInitials(name: string): string {
  const words = name
    .replace(/,.*$/, "")
    .split(/\s+/)
    .filter((w) => !HONORIFICS.test(w))
    .filter((w) => w.replace(/[.\-]/g, "").length > 1);

  const first = words[0]?.[0] ?? name[0] ?? "";
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
}

type Props = {
  name: string;
  /** Rendering context — the dark leadership band vs. light cards. */
  variant?: "dark" | "light";
  className?: string;
};

/**
 * Premium monogram avatar used while approved leadership photography is
 * pending. Keeps the leadership sections looking intentional and branded
 * instead of showing a broken image placeholder.
 */
export default function LeaderAvatar({ name, variant = "dark", className = "" }: Props) {
  const initials = getInitials(name);

  const surface =
    variant === "dark"
      ? "from-[#215a46] via-[#164034] to-[#0c2a1c]"
      : "from-[#1f5643] via-[#1a4d3c] to-[#123528]";

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center overflow-hidden bg-gradient-to-br ${surface} ${className}`}
      role="img"
      aria-label={name}
    >
      {/* Soft gold glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 30% 22%, rgba(201,169,97,0.22) 0%, transparent 45%)",
        }}
      />
      {/* Hairline gold frame */}
      <div className="absolute inset-4 rounded-lg border border-[#c9a961]/25" />

      <span className="relative font-['Cormorant_Garamond'] font-bold text-[#c9a961] tracking-wide select-none leading-none text-[clamp(3rem,7vw,4.75rem)]">
        {initials}
      </span>

      <span className="absolute bottom-5 left-0 right-0 text-center text-[10px] uppercase tracking-[0.32em] text-white/30 font-['Inter']">
        Harambee
      </span>
    </div>
  );
}
